import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { IPracticeTypeSwitch } from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import { requestPracticeTypeRun } from "../practiceTypeRunEvent";
import {
  buildPracticeTypeSwitchMessage,
  practiceTypeName,
} from "../practiceTypeSwitchMessage";
import { usePracticeTypeAutoRerun } from "../usePracticeTypeAutoRerun";

/**
 * CLE-2971 — "we switched the Service Type", beside that side's Service Type dropdown.
 *
 * The API switches the Service Type inside the eligibility run when the order's CPT codes say so. This
 * reads the switch off the stored response (`benefitDetails(2).response.practiceTypeSwitch`), so it
 * survives a reload and shows on draft / copy / redo pages without any request. Informational, with one
 * way back: "Keep …" re-runs the check under the original code with `keepPracticeType`, after which the
 * response carries no switch and the notice goes away by itself. Hidden once the estimator picks
 * another Service Type by hand: the notice would no longer describe the dropdown.
 *
 * Plain text with a small Keep button — a line under the dropdown, not a coloured box. The moment of the
 * switch itself is announced by a toast from `EligibilityButton`, because the estimator is then down at
 * the procedure table, not looking at the dropdown.
 *
 * Also hosts the page trigger (`usePracticeTypeAutoRerun`) for its side, and that trigger's own Toast:
 * its sticky "Re-running eligibility…" message must come down when the re-run settles, and PrimeReact 8
 * cannot remove one message from the shared app Toast, so it lives on a Toast that holds nothing else.
 */
export function PracticeTypeSwitchNotice({
  accountId,
  isSecondaryInsurance = false,
}: {
  accountId: string;
  isSecondaryInsurance?: boolean;
}) {
  const rerunToastRef = useRef<Toast>(null);
  usePracticeTypeAutoRerun({ accountId, isSecondaryInsurance, rerunToastRef });
  const { watch } = useFormContext();
  const benefitDetails = watch(
    isSecondaryInsurance ? "benefitDetails2" : "benefitDetails"
  );
  const practiceType = watch(
    isSecondaryInsurance ? "practiceType2" : "practiceType"
  );
  const practiceTypeSwitch: IPracticeTypeSwitch | null | undefined =
    benefitDetails?.response?.practiceTypeSwitch;

  // Portalled to the body, so the notice's own spot stays empty when there is nothing to say.
  const rerunToast = (
    <Toast
      ref={rerunToastRef}
      position="top-right"
      baseZIndex={1000}
      appendTo={document.body}
    />
  );

  if (
    !practiceTypeSwitch?.from ||
    !practiceTypeSwitch?.to ||
    practiceType !== practiceTypeSwitch.to
  )
    return rerunToast;

  return (
    <>
      {rerunToast}
      <div
        className="flex flex-column align-items-start gap-1 mb-3"
        data-testid={
          isSecondaryInsurance
            ? "practiceTypeSwitchNotice2"
            : "practiceTypeSwitchNotice"
        }
      >
        <span>{buildPracticeTypeSwitchMessage(practiceTypeSwitch)}</span>
        {practiceTypeSwitch.reason && (
          <span className="text-sm text-color-secondary">
            {practiceTypeSwitch.reason}
          </span>
        )}
        <Button
          type="button"
          label={`Keep ${practiceTypeName(practiceTypeSwitch.from)}`}
          className="p-button-sm p-button-outlined"
          data-testid="practiceTypeSwitchKeep"
          onClick={() =>
            requestPracticeTypeRun({
              isSecondaryInsurance,
              practiceTypeCode: practiceTypeSwitch.from,
              keepPracticeType: true,
            })
          }
        />
      </div>
    </>
  );
}
